import React, { useState, useEffect } from 'react';
import { useRange } from 'react-instantsearch';
import { FormattedMessage } from '../../../util/reactIntl';
import { Button, RangeSlider } from '../../../components';
import css from './Filters.module.css';
import { LISTING_SEARCH_ID } from '../../../constants';
import { createResourceLocatorString } from '../../../util/routes';

const unsetNumberInputValue = '';

function CustomRangeInput({ attribute, precision = 2 }) {
  const [error, setError] = useState(null);
  const { start, range, canRefine, precision: valuePrecision, refine } = useRange({
    attribute,
    precision,
  });

  const step = 1 / Math.pow(10, valuePrecision || 0);

  const initialValues = {
    min: start[0] !== -Infinity && start[0] !== range.min ? start[0] : unsetNumberInputValue,
    max: start[1] !== Infinity && start[1] !== range.max ? start[1] : unsetNumberInputValue,
  };

  const [prevValues, setPrevValues] = useState(initialValues);
  const [{ from, to }, setRange] = useState({
    from: initialValues.min?.toString(),
    to: initialValues.max?.toString(),
  });

  // Update state if values change
  useEffect(() => {
    if (initialValues.min !== prevValues.min || initialValues.max !== prevValues.max) {
      setRange({
        from: initialValues.min?.toString(),
        to: initialValues.max?.toString(),
      });
      setPrevValues(initialValues);
    }
  }, [initialValues, prevValues]);

  const [debouncedRange, setDebouncedRange] = useState({ from, to });
  useEffect(() => {
    const handler = setTimeout(() => {
      if (debouncedRange.from !== unsetNumberInputValue || debouncedRange.to !== unsetNumberInputValue) {
        refine([
          debouncedRange.from ? Math.max(Number(debouncedRange.from), range.min) : range.min,
          debouncedRange.to ? Math.min(Number(debouncedRange.to), range.max) : range.max,
        ]);
      }
    }, 800);

    return () => clearTimeout(handler);
  }, [debouncedRange, range.min, range.max, refine]);

  const handleInputChange = (value, isMin) => {
    const newRange = isMin ? { ...debouncedRange, from: value } : { ...debouncedRange, to: value };
    setRange(newRange);
    setDebouncedRange(newRange);
  };

  const handleSliderChange = (sliderValues) => {
    const [min, max] = sliderValues;
    const newRange = { from: min.toString(), to: max.toString() };

    setRange(newRange);
    setDebouncedRange(newRange);
    refine(sliderValues);
  };

  const validHandles =
    range && range.min
      ? [
          Math.max(Number(from), range.min) || range.min, // Limit `from` to at least `range.min`
          Math.min(Number(to), range.max) || range.max, // Limit `to` to at most `range.max`
        ]
      : [];

  return (
    <form
      onSubmit={event => {
        event.preventDefault();
      }}
      className={css.rangeFilter}
    >
      <label>
        <span>Price (USD)</span>
      </label>
      <div className={css.rangeFilterFieldsWrapper}>
        <div className={css.rangeFields}>
          <label>
            <input
              type="number"
              pattern="[0-9]*"
              inputMode="numeric"
              value={stripLeadingZeroFromInput(from || unsetNumberInputValue)}
              step={step}
              placeholder={(range.min).toString() || '0'}
              disabled={!canRefine}
              onChange={(e) => handleInputChange(e.target.value, true)}
              onKeyDown={(e) => {
                // Block non-numeric characters and special symbols
                const invalidChars = ['e', 'E', '+', '-', '\\', '/', '*', '='];
                if (invalidChars.includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </label>
          <span>
            <FormattedMessage id="RangeInputFilter.to" />
          </span>
          <label>
            <input
              type="number"
              pattern="[0-9]*"
              inputMode="numeric"
              value={stripLeadingZeroFromInput(to || unsetNumberInputValue)}
              step={step}
              placeholder={(range.max).toString() || '1'}
              disabled={!canRefine}
              onChange={(e) => handleInputChange(e.target.value, false)}
              onKeyDown={(e) => {
                // Block non-numeric characters and special symbols
                const invalidChars = ['e', 'E', '+', '-', '\\', '/', '*', '='];
                if (invalidChars.includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </label>
        </div>

        <RangeSlider
          min={range.min}
          max={range.max}
          step={step}
          handles={validHandles}
          onChange={handleSliderChange}
        />
        {error && <span className={css.error}>{error}</span>}
      </div>
      {/* <Button className={css.applyRange} type="submit">
        <FormattedMessage id="RangeInputFilter.apply" />
      </Button> */}
    </form>
  );
}

export default CustomRangeInput;

function stripLeadingZeroFromInput(value) {
  let updateValue = value ? Number(value) : null;
  return updateValue ? updateValue.toString().replace(/^0+/, '') : '';
}
